/**
 * InlineEditForm.tsx — Modal ligero de edición manual nombre/dirección (BUG-7b)
 *
 * Se muestra cuando `editingInfraId !== null`.
 * Expone campos «Nombre» y «Dirección» con los valores actuales de la infra.
 * Al guardar llama `onSave(infraId, { name, address })`.
 * Al cancelar llama `onCancel()`.
 *
 * Diseño:
 *  - Overlay semitransparente
 *  - Tarjeta centrada (max-width 480 px)
 *  - Tokens DS v2 (Space Grotesk, colores, radii)
 *  - Accesibilidad: role="dialog", aria-modal, Escape para cerrar, Ctrl+Enter para guardar
 *
 * @module wizard-v2/step2-valida/InlineEditForm
 */

import {
  useState,
  useEffect,
  useRef,
  type FC,
  type CSSProperties,
  type KeyboardEvent,
} from 'react';
import { colors, typography, borderRadius, transitions } from '@/design-system/v2';

// ============================================================================
// TYPES
// ============================================================================

export interface InlineEditFormProps {
  /** ID de la infraestructura que se está editando */
  infraId: string;
  /** Nombre actual */
  currentName: string;
  /** Dirección actual */
  currentAddress: string;
  /** Callback al guardar — recibe los valores actualizados */
  onSave: (infraId: string, data: { name: string; address: string }) => void;
  /** Callback al cancelar — cierra el modal sin guardar */
  onCancel: () => void;
}

// ============================================================================
// STYLES
// ============================================================================

const overlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.65)',
  zIndex: 9000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px',
};

const cardStyle: CSSProperties = {
  // VIS-2b-ii (Bucket A, DOP-BUCKETA=a): hex huérfano casi-duplicado (d=6) → bg.secondary, superficie elevada del DS
  background: colors.bg.secondary,
  border: `1px solid ${colors.border.DEFAULT}`,
  borderRadius: borderRadius.xl,
  padding: '28px 32px',
  width: '100%',
  maxWidth: '480px',
  boxShadow: '0 24px 64px rgba(0,0,0,.55)',
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
  fontFamily: typography.fontFamily.sans,
};

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const titleStyle: CSSProperties = {
  fontSize: typography.fontSize.lg,
  fontWeight: typography.fontWeight.semibold,
  color: colors.fg.primary,
  margin: 0,
};

const closeBtnStyle: CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  color: colors.fg.muted,
  padding: '4px',
  display: 'flex',
  alignItems: 'center',
  borderRadius: borderRadius.sm,
  transition: transitions.base,
};

const fieldGroupStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
};

const labelStyle: CSSProperties = {
  fontSize: typography.fontSize.xs,
  fontWeight: typography.fontWeight.medium,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: colors.fg.muted,
};

const inputStyle: CSSProperties = {
  background: colors.bg.tertiary,
  border: `1px solid ${colors.border.DEFAULT}`,
  borderRadius: borderRadius.md,
  padding: '10px 14px',
  color: colors.fg.primary,
  fontFamily: typography.fontFamily.sans,
  fontSize: typography.fontSize.base,
  outline: 'none',
  transition: transitions.base,
  width: '100%',
  boxSizing: 'border-box' as const,
};

const inputFocusStyle: CSSProperties = {
  borderColor: colors.primary,
  boxShadow: `0 0 0 2px rgba(45,212,191,0.2)`,
};

const footerStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '10px',
  marginTop: '4px',
};

const btnCancelStyle: CSSProperties = {
  padding: '9px 18px',
  borderRadius: borderRadius.md,
  border: `1px solid ${colors.border.DEFAULT}`,
  background: 'transparent',
  color: colors.fg.muted,
  fontFamily: typography.fontFamily.sans,
  fontSize: typography.fontSize.sm,
  fontWeight: typography.fontWeight.medium,
  cursor: 'pointer',
  transition: transitions.base,
};

const btnSaveStyle: CSSProperties = {
  padding: '9px 20px',
  borderRadius: borderRadius.md,
  border: 'none',
  background: colors.primary,
  color: '#18181b',
  fontFamily: typography.fontFamily.sans,
  fontSize: typography.fontSize.sm,
  fontWeight: typography.fontWeight.semibold,
  cursor: 'pointer',
  transition: transitions.base,
};

const hintStyle: CSSProperties = {
  fontSize: '11px',
  color: colors.fg.subtle,
  margin: 0,
  marginTop: '-8px',
};

const kbdStyle: CSSProperties = {
  fontFamily: typography.fontFamily.mono,
  fontSize: '10px',
  padding: '1px 4px',
  background: colors.bg.tertiary,
  borderRadius: '3px',
  border: `1px solid ${colors.border.DEFAULT}`,
};

// ============================================================================
// ICONS
// ============================================================================

function XIcon() {
  return (
    <svg viewBox="0 0 24 24" width={18} height={18} stroke="currentColor" strokeWidth={2} fill="none" aria-hidden="true">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

// ============================================================================
// COMPONENT
// ============================================================================

export const InlineEditForm: FC<InlineEditFormProps> = ({
  infraId,
  currentName,
  currentAddress,
  onSave,
  onCancel,
}) => {
  const [name, setName] = useState(currentName);
  const [address, setAddress] = useState(currentAddress);
  const [nameFocused, setNameFocused] = useState(false);
  const [addressFocused, setAddressFocused] = useState(false);

  const nameInputRef = useRef<HTMLInputElement>(null);

  // Foco inicial en el campo «Nombre» al montar
  useEffect(() => {
    nameInputRef.current?.focus();
    nameInputRef.current?.select();
  }, []);

  // Cerrar con Escape; Ctrl+Enter guarda
  const handleOverlayKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
  };

  const handleFormKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSave();
    }
  };

  const handleCardClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
  };

  const hasChanges =
    name.trim() !== currentName.trim() || address.trim() !== currentAddress.trim();
  const isValid = name.trim().length > 0;

  const handleSave = () => {
    if (!isValid) return;
    onSave(infraId, { name: name.trim(), address: address.trim() });
  };

  return (
    <div
      style={overlayStyle}
      onClick={onCancel}
      onKeyDown={handleOverlayKeyDown}
      role="presentation"
      data-testid="inline-edit-overlay"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="inline-edit-title"
        style={cardStyle}
        onClick={handleCardClick}
        onKeyDown={handleFormKeyDown}
        data-testid="inline-edit-form"
      >
        {/* Cabecera */}
        <div style={headerStyle}>
          <h2 id="inline-edit-title" style={titleStyle}>
            Editar infraestructura
          </h2>
          <button
            type="button"
            style={closeBtnStyle}
            onClick={onCancel}
            aria-label="Cerrar editor"
            title="Cancelar (Escape)"
          >
            <XIcon />
          </button>
        </div>

        {/* Campo Nombre */}
        <div style={fieldGroupStyle}>
          <label htmlFor="edit-name" style={labelStyle}>Nombre</label>
          <input
            ref={nameInputRef}
            id="edit-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onFocus={() => setNameFocused(true)}
            onBlur={() => setNameFocused(false)}
            style={nameFocused ? { ...inputStyle, ...inputFocusStyle } : inputStyle}
            placeholder="Nombre de la infraestructura"
            data-testid="edit-field-name"
            autoComplete="off"
          />
        </div>

        {/* Campo Dirección */}
        <div style={fieldGroupStyle}>
          <label htmlFor="edit-address" style={labelStyle}>Dirección</label>
          <input
            id="edit-address"
            type="text"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            onFocus={() => setAddressFocused(true)}
            onBlur={() => setAddressFocused(false)}
            style={addressFocused ? { ...inputStyle, ...inputFocusStyle } : inputStyle}
            placeholder="Dirección o referencia catastral"
            data-testid="edit-field-address"
            autoComplete="off"
          />
        </div>

        {/* Hint teclado */}
        <p style={hintStyle}>
          <kbd style={kbdStyle}>Ctrl</kbd>{' + '}
          <kbd style={kbdStyle}>Enter</kbd>{' para guardar · '}
          <kbd style={kbdStyle}>Esc</kbd>{' para cancelar'}
        </p>

        {/* Footer */}
        <div style={footerStyle}>
          <button
            type="button"
            style={btnCancelStyle}
            onClick={onCancel}
            data-testid="edit-btn-cancel"
          >
            Cancelar
          </button>
          <button
            type="button"
            style={hasChanges && isValid ? btnSaveStyle : { ...btnSaveStyle, opacity: 0.4, cursor: 'not-allowed' }}
            onClick={handleSave}
            disabled={!isValid || !hasChanges}
            data-testid="edit-btn-save"
            title={!hasChanges ? 'Sin cambios' : 'Guardar cambios'}
          >
            Guardar
          </button>
        </div>
      </div>
    </div>
  );
};

export default InlineEditForm;
